import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight,
  X,
  Target,
  Info,
  CheckCircle2
} from 'lucide-react';
import styles from './AppTour.module.css';

interface TourStep {
  id: string;
  target: string; // CSS selector do elemento
  title: string;
  description: string;
  tip: string;
  position: 'top' | 'bottom' | 'left' | 'right';
}

const tourSteps: TourStep[] = [
  {
    id: 'add-button',
    target: '[data-tour="add-habit"]',
    title: 'Criar Seu Primeiro Hábito Atômico',
    description: 'Clique aqui para adicionar um novo hábito. Lembre-se: comece pequeno! Em vez de "exercitar 1 hora", tente "fazer 5 flexões".',
    tip: 'Dica: Use a regra dos 2 minutos - qualquer hábito pode começar em menos de 2 minutos.',
    position: 'bottom'
  },
  {
    id: 'stats-completed',
    target: '[data-tour="stats-completed"]',
    title: 'Completados Hoje',
    description: 'Aqui você vê quantos hábitos já concluiu hoje. Cada ✓ libera dopamina e reforça o comportamento!',
    tip: 'Celebre cada pequena vitória. O progresso composto funciona através da consistência.',
    position: 'bottom'
  },
  {
    id: 'stats-streak',
    target: '[data-tour="stats-streak"]',
    title: 'Dias em Sequência',
    description: 'Sua sequência atual de dias consecutivos. Quanto maior, mais forte se torna o hábito automático.',
    tip: 'Não quebre a corrente! Se falhar um dia, recomece imediatamente no próximo.',
    position: 'bottom'
  },
  {
    id: 'stats-rate',
    target: '[data-tour="stats-rate"]',
    title: 'Taxa de Conclusão',
    description: 'A porcentagem de hábitos que você está conseguindo manter. Meta: 80% ou mais para mudança real.',
    tip: 'Foque na consistência, não na perfeição. 80% consistente > 100% esporádico.',
    position: 'bottom'
  },
  {
    id: 'habit-item',
    target: '[data-tour="habit-item"]',
    title: 'Marcar Como Concluído',
    description: 'Clique no círculo para marcar o hábito como feito. Toque no nome para ver detalhes e histórico.',
    tip: 'Marque imediatamente após concluir. A recompensa instantânea reforça o comportamento.',
    position: 'top'
  }
];

interface AppTourProps {
  isActive: boolean;
  onComplete: () => void;
}

const AppTour: React.FC<AppTourProps> = ({ isActive, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [adjustedPosition, setAdjustedPosition] = useState<'top' | 'bottom' | 'left' | 'right'>('bottom');

  const currentStepData = tourSteps[currentStep];
  const isLastStep = currentStep === tourSteps.length - 1;
  useEffect(() => {
    if (!isActive) return;

    const updateTooltipPosition = () => {
      const target = document.querySelector(currentStepData.target) as HTMLElement;
      if (!target) return;

      setTargetElement(target);
      const rect = target.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

      // Get viewport dimensions
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Tooltip dimensions (estimating based on content)
      const tooltipWidth = window.innerWidth <= 640 ? 240 : 280;
      const tooltipHeight = 200; // Approximate height
      
      // Buffer from screen edges
      const buffer = 16;

      let x = 0;
      let y = 0;
      let position = currentStepData.position;

      // Calculate initial position
      switch (position) {
        case 'top':
          x = rect.left + scrollLeft + rect.width / 2;
          y = rect.top + scrollTop - 20;
          break;
        case 'bottom':
          x = rect.left + scrollLeft + rect.width / 2;
          y = rect.bottom + scrollTop + 20;
          break;
        case 'left':
          x = rect.left + scrollLeft - 20;
          y = rect.top + scrollTop + rect.height / 2;
          break;
        case 'right':
          x = rect.right + scrollLeft + 20;
          y = rect.top + scrollTop + rect.height / 2;
          break;
      }

      // Check if tooltip would go off screen and adjust position
      if (position === 'top' || position === 'bottom') {
        // Check horizontal bounds
        const tooltipLeft = x - tooltipWidth / 2;
        const tooltipRight = x + tooltipWidth / 2;
        
        if (tooltipLeft < buffer) {
          x = buffer + tooltipWidth / 2;
        } else if (tooltipRight > viewportWidth - buffer) {
          x = viewportWidth - buffer - tooltipWidth / 2;
        }
        
        // Check vertical bounds
        if (position === 'top' && y - tooltipHeight < scrollTop + buffer) {
          // Switch to bottom if top doesn't fit
          position = 'bottom';
          y = rect.bottom + scrollTop + 20;
        } else if (position === 'bottom' && y + tooltipHeight > scrollTop + viewportHeight - buffer) {
          // Switch to top if bottom doesn't fit
          position = 'top';
          y = rect.top + scrollTop - 20;
        }
      } else {
        // Check vertical bounds for left/right positions
        const tooltipTop = y - tooltipHeight / 2;
        const tooltipBottom = y + tooltipHeight / 2;
        
        if (tooltipTop < scrollTop + buffer) {
          y = scrollTop + buffer + tooltipHeight / 2;
        } else if (tooltipBottom > scrollTop + viewportHeight - buffer) {
          y = scrollTop + viewportHeight - buffer - tooltipHeight / 2;
        }
        
        // Check horizontal bounds
        if (position === 'left' && x - tooltipWidth < scrollLeft + buffer) {
          // Switch to right if left doesn't fit
          position = 'right';
          x = rect.right + scrollLeft + 20;
        } else if (position === 'right' && x + tooltipWidth > scrollLeft + viewportWidth - buffer) {
          // Switch to left if right doesn't fit
          position = 'left';
          x = rect.left + scrollLeft - 20;
        }
      }      // Final check for mobile - use simplified positioning
      if (window.innerWidth <= 640) {
        // On mobile, use fixed positioning relative to viewport
        // The CSS will handle the horizontal centering
        if (rect.bottom + 150 < viewportHeight) {
          // Show below if there's space
          position = 'bottom';
          y = rect.bottom + 20;
        } else {
          // Show above if no space below
          position = 'top';
          y = Math.max(rect.top - 150, 20);
        }
        x = 0; // CSS will handle centering
      }setTooltipPosition({ x, y });
      setAdjustedPosition(position);

      // Highlight the target element
      target.style.position = 'relative';
      target.style.zIndex = '1001';
      target.style.outline = '2px solid #3b82f6';
      target.style.outlineOffset = '4px';
      target.style.borderRadius = '8px';
    };

    updateTooltipPosition();
    window.addEventListener('resize', updateTooltipPosition);
    window.addEventListener('scroll', updateTooltipPosition);

    return () => {
      window.removeEventListener('resize', updateTooltipPosition);
      window.removeEventListener('scroll', updateTooltipPosition);
      
      // Remove highlight
      if (targetElement) {
        targetElement.style.position = '';
        targetElement.style.zIndex = '';
        targetElement.style.outline = '';
        targetElement.style.outlineOffset = '';
        targetElement.style.borderRadius = '';
      }
    };
  }, [currentStep, isActive, currentStepData, targetElement]);

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  if (!isActive) return null;

  return (
    <>
      {/* Overlay */}
      <div className={styles.overlay} />
        {/* Tooltip */}
      <motion.div
        className={`${styles.tooltip} ${styles[`tooltip${adjustedPosition.charAt(0).toUpperCase() + adjustedPosition.slice(1)}`]}`}
        style={{
          left: tooltipPosition.x,
          top: tooltipPosition.y,
        }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        {/* Arrow */}
        <div className={`${styles.arrow} ${styles[`arrow${adjustedPosition.charAt(0).toUpperCase() + adjustedPosition.slice(1)}`]}`} />
        
        {/* Content */}
        <div className={styles.tooltipContent}>
          <div className={styles.tooltipHeader}>
            <div className={styles.stepIndicator}>
              <Target size={16} />
              <span>{currentStep + 1} de {tourSteps.length}</span>
            </div>
            <button onClick={handleSkip} className={styles.closeButton}>
              <X size={16} />
            </button>
          </div>

          <h3 className={styles.tooltipTitle}>{currentStepData.title}</h3>
          <p className={styles.tooltipDescription}>{currentStepData.description}</p>
          
          <div className={styles.tipCard}>
            <div className={styles.tipHeader}>
              <Info size={16} className={styles.tipIcon} />
              <span className={styles.tipLabel}>Dica dos Hábitos Atômicos</span>
            </div>
            <p className={styles.tipText}>{currentStepData.tip}</p>
          </div>

          <div className={styles.tooltipFooter}>
            <button onClick={handleSkip} className={styles.skipButton}>
              Pular Tour
            </button>
            <button onClick={handleNext} className={styles.nextButton}>
              {isLastStep ? (
                <>
                  <CheckCircle2 size={16} />
                  Finalizar
                </>
              ) : (
                <>
                  Próximo
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default AppTour;
